// import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { toast, useDebounce } from '@payloadcms/ui'

import { getUser, updateUser } from '@/app/actions/user'
import DynamicIcon, { IconName } from '@/components/DynamicIcon'
import Pill from '@/components/Pill'
import { Topic, User } from '@/payload-types'
// import { getClientSideURL } from '@/utilities/getURL'
import { cn } from '@/utilities/ui'

/** Normalize user.topics.topics (number | Topic)[] to Topic[] using the full topics list for ids */
function initialTopicsFromUser(
  userTopics: (number | Topic)[] | null | undefined,
  allTopics: Topic[],
): Topic[] {
  if (!userTopics || !Array.isArray(userTopics)) return []
  return userTopics
    .map((ref) => (typeof ref === 'number' ? allTopics.find((t) => t.id === ref) : ref))
    .filter((t): t is Topic => t != null)
}

function arraysEqual(a: Topic[], b: Topic[]): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) return false
  if (a.length !== b.length) return false
  return a.every((val, i) => val.id === b[i].id)
}

export const TopicForm = ({
  topics,
  user,
  onUserUpdated,
}: {
  topics: Topic[]
  user: User
  onUserUpdated?: (updatedUser?: User) => Promise<void>
}) => {
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>(() =>
    initialTopicsFromUser(user.topics?.topics, topics),
  )
  const debouncedSelectedTopics = useDebounce(selectedTopics, 1500)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const handleSelectTopic = (topic: Topic) => {
    if (selectedTopics.some((t) => t.id === topic.id)) {
      setSelectedTopics((prev) => prev.filter((t) => t.id !== topic.id))
    } else {
      setSelectedTopics((prev) => [...prev, topic])
    }
  }

  const handleSubmit = useCallback(async () => {
    setIsSaving(true)
    setSubmitError(null)
    try {
      const res = await updateUser({
        ...user,
        topics: {
          topics: selectedTopics.map((t) => t.id),
        },
      })

      if (res.status === 'error') {
        setSubmitError(res.message)
        return
      }

      const updatedUser = res.data

      setSubmitError(null)
      await onUserUpdated?.(updatedUser)
      toast.success('Topics saved')
    } catch (error: unknown) {
      if (error instanceof Error) {
        setSubmitError(error.message)
      } else {
        setSubmitError('An unknown error occurred')
      }
      toast.error('Failed to save topics')
    } finally {
      setIsSaving(false)
    }
  }, [selectedTopics, user, onUserUpdated])

  useEffect(() => {
    const run = async () => {
      const data = await getUser()

      if (data.status === 'error') {
        return
      }

      const user = data.data

      const userTopics = initialTopicsFromUser(user.topics?.topics, topics)

      if (arraysEqual(userTopics, debouncedSelectedTopics)) return

      handleSubmit()
    }
    run()
  }, [debouncedSelectedTopics, handleSubmit, topics])

  return (
    <div className="relative">
      {isSaving && (
        <div className="absolute inset-0 bg-white/25">
          {/* <Loader2 className="size-20 animate-spin" /> */}
        </div>
      )}
      <div className="mt-4 mb-8 flex flex-wrap justify-center gap-2">
        {topics.map((topic) => (
          <button
            key={topic.id}
            onClick={() => handleSelectTopic(topic)}
            className={cn(
              'cursor-pointer border-2 border-transparent',
              selectedTopics.some((t) => t.id === topic.id) && 'rounded-full border-evergreen-500',
            )}
          >
            <Pill color={topic.color ?? 'accent-foreground-3'}>
              <DynamicIcon name={topic.icon as IconName} className="size-4" />
              {topic.title}
            </Pill>
          </button>
        ))}
      </div>
      {submitError && (
        <p className="m-auto mt-4 rounded-md bg-red-50 p-3 text-center text-sm text-red-500">
          {submitError}
        </p>
      )}
    </div>
  )
}
